import { IRotateControl, IRotateArguments } from './rotate-control-interface';
import { Object3D, Vector3, Spherical } from 'three';
import { CLOSE_TO_ZERO } from '../../../utils/math-utils';

export interface IOrbitRotateArguments extends IRotateArguments {
    maxHorizontalAngle?: number;
    maxVerticalAngle?: number;
    minHorizontalAngle?: number;
    minVerticalAngle?: number;
    target?: Vector3;
}

export class GenericOrbitRotateControl implements IRotateControl {
    private localX: Vector3;
    private localY: Vector3;
    private localZ: Vector3;
    private offset: Vector3;
    private previousTarget: Vector3;
    private spherical: Spherical;

    public constructor() {
        this.localX = new Vector3();
        this.localY = new Vector3();
        this.localZ = new Vector3();

        this.offset = new Vector3();
        this.previousTarget = new Vector3();
        this.spherical = new Spherical();
    }

    public rotate(
        object: Object3D,
        rotateArguments: IOrbitRotateArguments = {}
    ): void {
        const {
            horizontalAngle = 0,
            rotateSpeed = 1,
            target = this.previousTarget,
            verticalAngle = 0,
            maxHorizontalAngle = Infinity,
            maxVerticalAngle = Math.PI,
            minHorizontalAngle = -Infinity,
            minVerticalAngle = 0,
        } = rotateArguments;
        object.updateMatrixWorld();
        if (horizontalAngle || verticalAngle) {
            this.offset
                .copy(target)
                .applyMatrix4(object.matrixWorld.clone().invert());
            let rotatingAroundSelf = false;
            if (this.offset.lengthSq() <= CLOSE_TO_ZERO) {
                // rotating around self so do math around unit sphere
                object.matrix.extractBasis(
                    this.localX,
                    this.localY,
                    this.localZ
                );
                this.offset.copy(this.localZ).negate();
                rotatingAroundSelf = true;
            }

            this.spherical.setFromVector3(this.offset);
            this.spherical.theta += horizontalAngle * rotateSpeed;
            this.spherical.phi += verticalAngle * rotateSpeed;
            this.spherical.theta = Math.max(
                minHorizontalAngle,
                Math.min(maxHorizontalAngle, this.spherical.theta)
            );
            this.spherical.phi = Math.max(
                minVerticalAngle,
                Math.min(maxVerticalAngle, this.spherical.phi)
            );
            this.spherical.makeSafe();
            this.spherical.radius = this.offset.length();
            this.offset.setFromSpherical(this.spherical);
            object.position.addVectors(target, this.offset);
            object.updateMatrixWorld(true);
            object.up.set(0, 1, 0);
            object.lookAt(target);
            if (rotatingAroundSelf) {
                object.position.copy(target);
            }
            object.updateMatrixWorld();
            this.previousTarget.copy(target);
        }
    }
}
